import {
  checkboxFilter,
  dateFilter,
  type DateFilterValue,
  type FilterDefs,
  multiFilter,
  useFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useToDoModal } from "src/components/to-dos/ToDoModal";
import type {
  ToDoModal_CohortFragment,
  ToDoModal_DevelopmentFragment,
  ToDoModal_MarketFragment,
  ToDoModal_ProjectFragment,
} from "src/components/to-dos/ToDoModal.generated";
import { type ToDoCardFragment, useToDosQuery } from "src/components/to-dos/ToDosBoard.generated";
import { ToDosTable } from "src/components/to-dos/ToDosTable";
import type { InternalUserDetailFragment } from "src/fragments/circularFragments.generated";
import { type DateFilter2, type ToDoFilter, ToDoStatus } from "src/generated/graphqlTypes";
import { useToDoTypes } from "src/hooks/enums/useToDoTypes";
import { useMatchAny } from "src/hooks/useMatchAny";
import { useCurrentInternalUserQuery } from "src/queries/user.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PersonalDashboardTitle } from "src/routes/personal-dashboard/components/PersonalDashboardTitle";
import { useDashboardFilterContext } from "src/routes/personal-dashboard/DashboardFilterContext";
import type { PersonalDashboardFilter } from "src/routes/personal-dashboard/useHeaderFilterDefs";
import { personalDashboardPaths } from "src/routes/routesDef";
import { createProjectToDosUrl, createToDosPdfUrl } from "src/RouteUrls";
import { projectToDoModalRoute } from "src/routing/domains/projectsPaths";
import { dateFilterOperations, isEmpty, nonEmpty } from "src/utils";
import { openNewTab } from "src/utils/window";

export type ToDoFilterWithDateFilterValue = Omit<ToDoFilter, "dueDate" | "assignee"> & {
  dueDate?: DateFilterValue<string>;
  internalUser?: string[];
};

export type ToDoBoardProps = {
  projectId?: string;
  projects?: ToDoModal_ProjectFragment[];
  cohorts?: ToDoModal_CohortFragment[];
  developments?: ToDoModal_DevelopmentFragment[];
  markets?: ToDoModal_MarketFragment[];
  teamMembers: InternalUserDetailFragment[];
};

/** Displays the To-Do table and its filters for projects and the personal dashboard. */
export function ToDosBoard(props: ToDoBoardProps) {
  // Use dashboard filters on both the To-Dos page and its modal routes.
  const isDashboardTodosPage = !!useMatchAny([personalDashboardPaths.toDos, personalDashboardPaths.toDoModal]);
  return isDashboardTodosPage ? <DashboardToDosTable {...props} /> : <ProjectToDosTable {...props} />;
}

/**
 * Owns the persisted project filters and the table search.
 * The dashboard uses its own header filters instead.
 */
function ProjectToDosTable(props: ToDoBoardProps) {
  const { projectId, projects, cohorts, developments, markets, teamMembers } = props;
  const toDoTypes = useToDoTypes();

  const projectOptions = useMemo(() => projects ?? [], [projects]);
  const cohortOptions = useMemo(() => cohorts ?? [], [cohorts]);
  const developmentOptions = useMemo(() => developments ?? [], [developments]);
  const marketOptions = useMemo(() => markets ?? [], [markets]);

  const { data: currentInternalUserData } = useCurrentInternalUserQuery({ fetchPolicy: "cache-first" });
  const currentUser = currentInternalUserData?.currentInternalUser;
  const isProjectPage = !!projectId;
  const defaultUserValue = useMemo(() => {
    return currentUser && !isProjectPage ? [currentUser.id] : undefined;
  }, [currentUser, isProjectPage]);

  const filterDefs: FilterDefs<ToDoFilterWithDateFilterValue> = useMemo(() => {
    const currentTeamMember = teamMembers.find((member) => member.id === currentUser?.id);
    const otherTeamMembers = teamMembers.filter((member) => member.id !== currentUser?.id);
    // Put (Me) option first
    const teamMemberOptions = currentTeamMember ? [currentTeamMember, ...otherTeamMembers] : teamMembers;
    return {
      internalUser: multiFilter({
        label: "Assigned to",
        options: teamMemberOptions,
        getOptionValue: (member) => member.id,
        getOptionLabel: (member) => (member.id === currentUser?.id ? `${member.name} (Me)` : member.name),
        defaultValue: defaultUserValue,
      }),
      ...(!isProjectPage
        ? {
            project: multiFilter({
              options: projectOptions,
              getOptionValue: (project) => project.id,
              getOptionLabel: (project) => project.name,
            }),
            development: multiFilter({
              options: developmentOptions,
              getOptionValue: (development) => development.id,
              getOptionLabel: (development) => development.name,
            }),
            cohort: multiFilter({
              options: cohortOptions,
              getOptionValue: (cohort) => cohort.id,
              getOptionLabel: (cohort) => cohort.name,
            }),
            market: multiFilter({
              options: marketOptions,
              getOptionValue: (market) => market.id,
              getOptionLabel: (market) => market.name,
            }),
          }
        : {}),
      type: multiFilter({
        options: toDoTypes ?? [],
        getOptionValue: (type) => type.code,
        getOptionLabel: (type) => type.name,
      }),
      dueDate: dateFilter({
        label: "Due Date",
        operations: dateFilterOperations,
        getOperationLabel: (operation) => operation.label,
        getOperationValue: (operation) => operation.value,
      }),
      createdBy: multiFilter({
        label: "Created by",
        options: teamMemberOptions,
        getOptionValue: (member) => member.id,
        getOptionLabel: (member) => (member.id === currentUser?.id ? `${member.name} (Me)` : member.name),
        defaultValue: defaultUserValue,
      }),
      watching: checkboxFilter({ label: "Watching" }),
      urgent: checkboxFilter({ label: "Urgent" }),
    };
  }, [
    defaultUserValue,
    isProjectPage,
    teamMembers,
    marketOptions,
    projectOptions,
    cohortOptions,
    developmentOptions,
    toDoTypes,
    currentUser,
  ]);

  const layoutState = useGridTableLayoutState<ToDoFilterWithDateFilterValue>({
    persistedFilter: { storageKey: "projectToDoFilter", filterDefs },
    // Search is applied to the loaded To-Dos so export uses the same matching records.
    search: "server",
  });
  return <ToDosContent {...props} layoutState={layoutState} />;
}

type ToDosContentProps = ToDoBoardProps & {
  layoutState: ReturnType<typeof useGridTableLayoutState<ToDoFilterWithDateFilterValue>>;
  isDashboardTodosPage?: boolean;
};

/**
 * Loads active and completed To-Dos for the selected filters.
 * Connects table actions to the modal, completed-item loading, and PDF export.
 */
function ToDosContent(props: ToDosContentProps) {
  const { projectId, projects, cohorts, developments, teamMembers, layoutState, isDashboardTodosPage = false } = props;
  const isProjectPage = !!projectId;
  const toDoTypes = useToDoTypes();
  const parentOptions = [...(projects ?? []), ...(cohorts ?? []), ...(developments ?? [])];
  const { filter } = useDashboardFilterContext();
  // if we are on dashboards page, then use the filter from dashboards context, else use the regular Todo filter
  const todosFilter = isDashboardTodosPage ? filter : layoutState.filter;

  const projectsForCohorts = projects
    ?.filter((project) => {
      return todosFilter.cohort?.some((cohortId) => {
        return cohortId === project.cohort?.id;
      });
    })
    .map((project) => project.id);
  const cohortsForDevelopments = cohorts
    ?.filter((cohort) => {
      return todosFilter.development?.some((developmentId) => {
        return developmentId === cohort.development?.id;
      });
    })
    .map((cohort) => cohort.id);
  const projectsForDevelopments = projects
    ?.filter((project) => {
      return cohortsForDevelopments?.some((cohortId) => {
        return project?.cohort?.id === cohortId;
      });
    })
    .map((project) => project.id);

  const queryFilter = mapToFilter(
    todosFilter,
    showAllStatuses,
    projectId,
    projectsForCohorts || [],
    projectsForDevelopments || [],
    cohortsForDevelopments || [],
  );
  const completedQueryFilter = mapToFilter(
    todosFilter,
    [ToDoStatus.Complete],
    projectId,
    projectsForCohorts || [],
    projectsForDevelopments || [],
    cohortsForDevelopments || [],
  );
  const query = useToDosQuery({ variables: { filter: queryFilter }, fetchPolicy: "cache-and-network" });

  const completedToDosQuery = useToDosQuery({
    variables: { filter: completedQueryFilter, first: 0 },
    fetchPolicy: "cache-and-network",
  });
  const { data } = query;
  const { data: completedData, fetchMore } = completedToDosQuery;
  const toDos = useMemo(() => data?.toDos || [], [data]);
  const completedToDos = useMemo(() => completedData?.toDos || [], [completedData]);
  const filteredToDos = searchToDos(uniqBy([...completedToDos, ...toDos], "id"), layoutState.searchString);
  const completedLength = completedToDos?.length;
  const showLoadMore = completedLength % loadMoreIncrement === 0 && completedLength !== 0;
  const toDoModalMatch = useMatchAny([personalDashboardPaths.toDoModal, projectToDoModalRoute.path]);
  const toDoId = toDoModalMatch?.params?.toDoId;

  const { open } = useToDoModal({
    isProjectPage,
    defaultParentId: projectId,
    returnUrl: isDashboardTodosPage
      ? personalDashboardPaths.toDos
      : projectId
        ? createProjectToDosUrl(projectId)
        : undefined,
    refreshQueries: () => {
      void query.refetch();
      void completedToDosQuery.refetch();
    },
  });

  // Open toDoModal from route param
  useEffect(() => {
    if (toDoId) {
      open(toDoId, projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toDoId]);

  const fetchMoreCompletedTasks = useCallback(
    () => fetchMore({ variables: { first: completedToDos.length } }),
    [fetchMore, completedToDos.length],
  );
  /** Exports the To-Dos that match the current filters and search. */
  function handleExportClick() {
    if (filteredToDos) {
      openNewTab(createToDosPdfUrl(filteredToDos.map((toDo) => toDo.id)));
    }
  }

  return (
    <>
      {isDashboardTodosPage && <PageHeader title="To-Dos" hideTitle left={<PersonalDashboardTitle title="To-Dos" />} />}
      <ToDosTable
        projectId={projectId}
        toDos={filteredToDos}
        parentOptions={parentOptions}
        teamMembers={teamMembers}
        types={toDoTypes}
        fetchMoreCompletedTasks={fetchMoreCompletedTasks}
        showLoadMore={showLoadMore}
        loading={query.loading || completedToDosQuery.loading}
        error={query.error || completedToDosQuery.error}
        layoutState={layoutState}
        actions={[
          {
            label: "Export",
            variant: "secondary",
            disabled: isEmpty(filteredToDos),
            icon: "upload",
            onClick: handleExportClick,
          },
          { label: "Add To-Do", onClick: () => open("new") },
        ]}
      />
    </>
  );
}

/**
 * Reuses dashboard filters without mounting a second persisted filter.
 * Supplies search state to the Beam table toolbar.
 */
function DashboardToDosTable(props: ToDoBoardProps) {
  const [searchString, setSearchString] = useState<string | undefined>();
  const { filter, setFilter } = useFilter<ToDoFilterWithDateFilterValue>({ filterDefs: {} });
  return (
    <ToDosContent
      {...props}
      isDashboardTodosPage
      layoutState={{
        filter,
        setFilter,
        filterDefs: undefined,
        searchString,
        setSearchString,
        search: "server",
        groupBy: undefined,
        visibleColumnIds: undefined,
        setVisibleColumnIds: undefined,
        persistedColumnsStorageKey: undefined,
        filteringActive: !!searchString,
        clearFilters: () => setSearchString(undefined),
      }}
    />
  );
}

/** Searches To-Do details, parents, types, and assignees. */
export function searchToDos(toDos: ToDoCardFragment[], searchFilter?: string) {
  return searchFilter
    ? toDos.filter((t) =>
        [t.name, t.description, t.parent?.name, t.type.name, ...t.assignees.map((assignee) => assignee.name)]
          .map((s) => s?.toLowerCase())
          .some((s) => s?.includes(searchFilter!.toLowerCase())),
      )
    : toDos;
}

/**
 * Converts the page filters to the To-Do query filter.
 * Includes child projects and cohorts when filtering by their parents.
 */
function mapToFilter(
  filter: ToDoFilterWithDateFilterValue | PersonalDashboardFilter,
  statuses: ToDoStatus[],
  projectId: string | undefined,
  projectsForCohorts: string[],
  projectsForDevelopments: string[],
  cohortsForDevelopments: string[],
): ToDoFilter {
  const { dueDate, cohort, project, development, internalUser, ...others } = filter;

  let projectIds: string[] = [];
  let cohortIds: string[] = [];
  let developmentIds: string[] = [];

  if (projectId) {
    projectIds = [projectId];
  }
  if (project) {
    projectIds = [...project];
  }
  if (cohort) {
    projectIds = [...projectIds, ...projectsForCohorts];
    cohortIds = [...cohort];
  }
  if (development) {
    projectIds = [...projectIds, ...projectsForDevelopments];
    cohortIds = [...cohortIds, ...cohortsForDevelopments];
    developmentIds = [...development];
  }

  return {
    status: statuses,
    ...others,
    project: nonEmpty(projectIds) ? projectIds.unique() : undefined,
    cohort: nonEmpty(cohortIds) ? cohortIds.unique() : undefined,
    development: nonEmpty(developmentIds) ? developmentIds : undefined,
    ...(dueDate ? { dueDate: { op: dueDate.op, value: dueDate.value } as DateFilter2 } : {}),
    assignee: internalUser,
  };
}

const loadMoreIncrement = 10;
export const showAllStatuses = [ToDoStatus.Backlog, ToDoStatus.UpNext, ToDoStatus.InProgress];
